// BuilderHeader - 上部ヘッダー（spec.md §5.8.1）
// なまえ（タップでピース情報モーダル）/ サイズ / AI / テスト / ヘルプ。
// なまえ・せつめい・ヒントの編集は「ピース情報」モーダルに集約。
// もどる・ユーザーアバターはアプリ共通 chrome が描画するため本ヘッダーには持たない。
// 必須条件・コマンド設定は PublishStepsPanel（§5.8.3/§5.8.7）が担う。

import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import GridViewIcon from "@mui/icons-material/GridView";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import { RootState } from "app/store";
import I18n from "common/i18n";
import { AssetModelType } from "common/model";
import { useScale, usePlaySound, btnDelay } from "common/utils";
import { PieceStatus } from "features/builder/types";
import { selectAllMapModel } from "features/builder/slice";
import { EDITOR_GUIDE_CHAPTERS } from "features/builder/guideBookPages";
import { StatusBadge, GuideBookId, BuilderGuideBook } from "features/builder/elements";
import { useStartTest } from "./useStartTest";
import { SizeChangeModal } from "./SizeChangeModal";
import { AIMapGeneratorModal } from "./AIMapGeneratorModal";
import { CommandSettingsModal } from "./CommandSettingsModal";
import {
  TUTORIAL_DONE_KEY,
  BuilderGuideTargetId,
  BuilderTutorialOverlay,
} from "./BuilderTutorialOverlay";

// なまえ右に出す現在のステータスバッジ設定（一覧カードの StatusBadge と同じラベル・配色）
const STATUS_BADGE: Partial<
  Record<PieceStatus, { icon: string; labelKey: string; pillClass: string; textClass: string }>
> = {
  [PieceStatus.PUBLISHED]: {
    icon: "public",
    labelKey: "MSG_BUILDER_CARD_STATUS_PUBLISHED",
    pillClass: "bg-green-100",
    textClass: "text-white",
  },
  [PieceStatus.UNPUBLISHED]: {
    icon: "lock",
    labelKey: "MSG_BUILDER_CARD_STATUS_UNPUBLISHED",
    pillClass: "bg-[#9CA3AF]",
    textClass: "text-white",
  },
  [PieceStatus.EDITING]: {
    icon: "edit",
    labelKey: "MSG_BUILDER_CARD_STATUS_EDITING",
    pillClass: "bg-[#9BBFD4]",
    textClass: "text-white",
  },
  [PieceStatus.DRAFT_NEW]: {
    icon: "pending",
    labelKey: "MSG_BUILDER_CARD_STATUS_INCOMPLETE",
    pillClass: "bg-beige-60",
    textClass: "text-textcolor-black/70",
  },
};

export const BuilderHeader = ({ onEditInfo }: { onEditInfo: () => void }) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const startTest = useStartTest();
  const allMapModel = useSelector(selectAllMapModel);
  const size = useSelector((state: RootState) => state.builder.size);
  const mapName = useSelector((state: RootState) => state.builder.name);
  const status = useSelector((state: RootState) => state.builder.status);
  const statusBadge = STATUS_BADGE[status];

  const [showAIModal, setShowAIModal] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  // ヘルプボタンは画面全体の手引き。初回のスポットライトとは別物
  const [showGuideBook, setShowGuideBook] = useState(false);
  const [showSizeModal, setShowSizeModal] = useState(false);
  const [showCommandModal, setShowCommandModal] = useState(false);

  // 編集画面の初回だけガイドを自動で開く（spec.md §5.8.6）。
  // 2回目以降は localStorage で抑止し、ヘルプの手引きの「ガイドを もういちど」から開き直せる
  useEffect(() => {
    if (window.localStorage.getItem(TUTORIAL_DONE_KEY)) return;
    setShowTutorial(true);
  }, []);

  // テスト活性条件（キャラ + ゴール配置）
  const hasCharacter = allMapModel.some((m) => m.type === AssetModelType.CHARACTER);
  const hasGoal = allMapModel.some((m) => m.type === AssetModelType.GOAL);
  const canTest = hasCharacter && hasGoal;

  const handleOpenAI = () => {
    play();
    btnDelay(() => setShowAIModal(true));
  };

  const handleOpenSize = () => {
    play();
    btnDelay(() => setShowSizeModal(true));
  };

  const handleOpenCommand = () => {
    play();
    btnDelay(() => setShowCommandModal(true));
  };

  const handleEditInfo = () => {
    play();
    btnDelay(() => onEditInfo());
  };

  const handleTest = () => {
    if (!canTest) return;
    play();
    btnDelay(() => startTest());
  };

  // ヘルプは手引き（ガイドブック）を開く。スポットライトは手引きの見出しから開き直す
  const handleOpenTutorial = () => {
    play();
    btnDelay(() => setShowGuideBook(true));
  };

  return (
    <>
      {/* 中央揃えコンテナ（最上部、アプリ共通 もどる/アバターの右に並ぶ） */}
      <div
        className="flex-row-el flex-center absolute left-0 right-0 z-[9000] pointer-events-none w-full"
        style={{ top: 8 * scale }}
      >
        {/* scale ラッパー（ToolController と同じ: origin-top-left で base サイズを scale） */}
        <div
          className="flex-row-el pointer-events-auto"
          style={{ width: 860 * scale, height: 136 * scale }}
        >
          <div
            className="flex-row-el items-center gap-3 origin-top-left w-[860px] h-[136px]"
            style={{ transform: `scale(${scale})` }}
          >
            {/* 白ピル（2行: 1段目=なまえ+くわしく / 2段目=道具5ボタン） */}
            <div
              data-guide="editor_header"
              className="flex-col-el justify-center gap-2 flex-1 h-full px-4 py-3 rounded-2xl bg-white/95 backdrop-blur-sm border-4 border-white shadow-lg"
            >
              {/* 1段目: なまえ + ステータス + くわしく */}
              <div className="flex-row-el items-center gap-3 w-full">
                {/* なまえ（表示）＋ ステータスバッジ */}
                <div className="flex-row-el items-center gap-2 flex-1 min-w-0">
                  <span
                    className={`type-md min-w-0 truncate px-3 ${
                      mapName ? "text-textcolor-black" : "text-textcolor-black/40"
                    }`}
                  >
                    {mapName || I18n.t("MSG_BUILDER_MAP_NAME_PLACEHOLDER")}
                  </span>
                  {statusBadge && (
                    <StatusBadge
                      icon={statusBadge.icon}
                      labelKey={statusBadge.labelKey}
                      pillClass={statusBadge.pillClass}
                      textClass={statusBadge.textClass}
                      iconOnly={false}
                    />
                  )}
                </div>

                {/* くわしく（ピース情報モーダル） */}
                <button
                  onClick={handleEditInfo}
                  className="flex-row-el flex-center gap-1 h-12 px-4 rounded-xl bg-blue-10 hover:bg-blue-20 border-2 border-blue-60 transition-colors active:scale-95 type-inherit-font whitespace-nowrap"
                >
                  <span className="flex-col-el flex-center type-title text-blue-100">
                    <EditOutlinedIcon fontSize="inherit" />
                  </span>
                  <span className="type-body text-textcolor-black">
                    {I18n.t("MSG_BUILDER_META_DETAIL_BTN")}
                  </span>
                </button>
              </div>

              {/* 2段目: サイズ / コマンド / 自動生成 / テスト / ヘルプ */}
              <div className="flex-row-el items-center gap-3 w-full">
                {/* サイズ（タップでサイズ変更モーダル） */}
                <button
                  data-guide="header_size"
                  onClick={handleOpenSize}
                  aria-label={I18n.t("MSG_BUILDER_SIZE_MODAL_TITLE")}
                  className="flex-row-el flex-center gap-1 h-12 px-4 rounded-xl bg-blue-10 hover:bg-blue-20 border-2 border-blue-60 transition-colors active:scale-95 type-inherit-font whitespace-nowrap"
                >
                  <span className="flex-col-el flex-center type-title text-blue-100">
                    <GridViewIcon fontSize="inherit" />
                  </span>
                  <span className="type-body text-textcolor-black">
                    {size.x}×{size.z}
                  </span>
                </button>

                {/* コマンド（タップでコマンド設定モーダル） */}
                <button
                  onClick={handleOpenCommand}
                  className="flex-row-el flex-center gap-1 h-12 px-4 rounded-xl bg-blue-10 hover:bg-blue-20 border-2 border-blue-60 transition-colors active:scale-95 type-inherit-font whitespace-nowrap"
                >
                  <span className="flex-col-el flex-center type-title text-blue-100">
                    <ExtensionOutlinedIcon fontSize="inherit" />
                  </span>
                  <span className="type-body text-textcolor-black">
                    {I18n.t("MSG_BUILDER_COMMAND_BTN")}
                  </span>
                </button>

                {/* 自動生成（AI でつくる） */}
                <button
                  onClick={handleOpenAI}
                  className="flex-row-el flex-center gap-1 h-12 px-4 rounded-xl bg-green-40 hover:bg-green-60 border-2 border-green-60 transition-colors active:scale-95 type-inherit-font whitespace-nowrap"
                >
                  <span className="flex-col-el flex-center type-title text-green-100">
                    <SmartToyOutlinedIcon fontSize="inherit" />
                  </span>
                  <span className="type-body text-textcolor-black">
                    {I18n.t("MSG_BUILDER_AI_GENERATE_BTN")}
                  </span>
                </button>

                {/* テスト */}
                <button
                  id={BuilderGuideTargetId.TEST_BTN}
                  onClick={handleTest}
                  disabled={!canTest}
                  className={`flex-row-el flex-center gap-1 h-12 px-4 rounded-xl border-2 transition-colors active:scale-95 type-inherit-font whitespace-nowrap ${
                    canTest
                      ? "bg-orange-100 hover:bg-orange-80 border-orange-100"
                      : "bg-gray-10 border-gray-20 cursor-not-allowed opacity-50"
                  }`}
                >
                  <span
                    className={`flex-col-el flex-center type-title ${
                      canTest ? "text-white" : "text-gray-80"
                    }`}
                  >
                    <PlayArrowIcon fontSize="inherit" />
                  </span>
                  <span className={`type-body ${canTest ? "text-white" : "text-gray-80"}`}>
                    {I18n.t("MSG_BUILDER_TEST_BTN")}
                  </span>
                </button>

                {/* ヘルプ（パネル内に復帰。アイコン+ラベル） */}
                <button
                  data-guide="header_help"
                  onClick={handleOpenTutorial}
                  className="flex-row-el flex-center gap-1 h-12 px-4 rounded-xl bg-blue-10 hover:bg-blue-20 border-2 border-blue-60 transition-colors active:scale-95 type-inherit-font whitespace-nowrap"
                >
                  <span className="flex-col-el flex-center type-title text-blue-100">
                    <HelpOutlineIcon fontSize="inherit" />
                  </span>
                  <span className="type-body text-textcolor-black">
                    {I18n.t("MSG_BUILDER_HELP_BTN")}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* モーダル */}
      <AIMapGeneratorModal open={showAIModal} onClose={() => setShowAIModal(false)} />
      <SizeChangeModal open={showSizeModal} onClose={() => setShowSizeModal(false)} />
      <CommandSettingsModal open={showCommandModal} onClose={() => setShowCommandModal(false)} />
      <BuilderTutorialOverlay open={showTutorial} onClose={() => setShowTutorial(false)} />

      <BuilderGuideBook
        open={showGuideBook}
        book={GuideBookId.EDITOR}
        chapters={EDITOR_GUIDE_CHAPTERS}
        onClose={() => setShowGuideBook(false)}
        onReplayGuide={() => {
          setShowGuideBook(false);
          setShowTutorial(true);
        }}
      />
    </>
  );
};
